import { RadioGroup } from 'src/core/forms';
import { Radio } from 'src/core/forms';
import { swatchCssNumerical, SwatchKindNumerical } from 'src/components/ProductDetail/utils/swatchData';
import React, { Dispatch, useState, SetStateAction, useEffect } from 'react';
import { ColourPickerSwatch } from 'src/components/ProductDetail/utils/product-detail.types';
import { useProductColorSwatches } from 'src/components/CategoryListingPage/ColorSwatchesProvider';
import { StaticSpinner } from 'src/components/Layout/Spinner';
import { useViewport } from 'src/hooks/useViewport';
import classnames from 'classnames';

import { Svg } from '../Svg/Svg';
import { SwatchOption } from '../Swatch/SwatchOption';

import classes from './ProductListColourPicker.scss';

const NR_OF_SWATCHES = 8;

// `value` is the swatch hex or image, which colours can share, so match on `color_id`
const findColor = (colorId: string, colorSwatches: ColourPickerSwatch[]) =>
    colorSwatches.find((color) => color.color_id === colorId);

const findBlackColor = (colorSwatches) => {
    return colorSwatches.find((color) => color.label.toLowerCase() === 'black') || null;
};

type Props = {
    onChange: Dispatch<SetStateAction<ColourPickerSwatch | null>>;
    onDefaultColorLoad?: Dispatch<SetStateAction<ColourPickerSwatch | null>>;
    colorSelections?: string[];
    setOptions?: (data: any) => void;
    productId: number;
    noBreakLines?: boolean;
    setSwatches?: Dispatch<SetStateAction<ColourPickerSwatch[]>>;
};

export const ProductListColourPicker: React.FC<Props> = ({
    onChange,
    onDefaultColorLoad,
    colorSelections,
    setOptions,
    productId,
    noBreakLines = false,
    setSwatches,
}) => {
    const { isMobile } = useViewport();
    const [colorSelectionsLength, setColorSelectionsLength] = useState<number>();
    const [firstIndex, setFirstIndex] = useState(0);
    const [lastIndex, setLastIndex] = useState(NR_OF_SWATCHES);

    // Fetched once for the whole list by ColorSwatchesProvider
    const { loading, product } = useProductColorSwatches(productId);

    useEffect(() => {
        if (product?.color_swatches && product.color_swatches.length > 0) {
            const colorSwatches = product.color_swatches;
            if (setSwatches) {
                setSwatches(colorSwatches);
            }

            // Find black or fallback to first color
            const blackColor = findBlackColor(colorSwatches);
            const selectedColor = blackColor || colorSwatches[0];

            setOptions?.(selectedColor);
            onDefaultColorLoad?.(selectedColor);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product]);

    // Every state renders in the same fixed-height slot so the card doesn't resize when swatches load.
    const slotClass = classnames(classes.slot, { [classes.slotNoBreakLines]: noBreakLines });

    if (!product && loading) {
        return (
            <div className={classnames(slotClass, classes.slotLoading)}>
                <StaticSpinner />
            </div>
        );
    }

    const colorSwatches = product?.color_swatches;

    if (colorSelections && colorSelectionsLength !== colorSelections?.length) {
        setColorSelectionsLength(colorSelections?.length);
    }

    if (!colorSwatches || colorSwatches.length <= 1) return <div className={slotClass} />;

    const maxIndex = colorSwatches.length;
    const displayColors = colorSwatches.slice(firstIndex, lastIndex);

    const handlePrevButtonClick = () => {
        if (firstIndex - NR_OF_SWATCHES >= 0) {
            setFirstIndex(firstIndex - NR_OF_SWATCHES);
            setLastIndex(lastIndex - NR_OF_SWATCHES);
        }

        if (firstIndex - NR_OF_SWATCHES < 0) {
            setFirstIndex(0);
            setLastIndex(NR_OF_SWATCHES);
        }
    };

    const handleNextButtonClick = () => {
        if (lastIndex + NR_OF_SWATCHES <= maxIndex) {
            setFirstIndex(firstIndex + NR_OF_SWATCHES);
            setLastIndex(lastIndex + NR_OF_SWATCHES);
        }

        if (lastIndex + NR_OF_SWATCHES > maxIndex) {
            setFirstIndex(maxIndex - NR_OF_SWATCHES);
            setLastIndex(maxIndex);
        }
    };

    return (
        <div className={slotClass}>
            <div className={classnames(classes.pickerContainer, { [classes.noBreakLines]: noBreakLines })}>
                {colorSwatches.length > NR_OF_SWATCHES && (
                    <button
                        type="button"
                        className={classes.paginationButtonLeft}
                        onClick={handlePrevButtonClick}
                        disabled={firstIndex === 0}
                        aria-label="Previous colours"
                    >
                        <Svg name="arrow-left" className={classes.svg} />
                    </button>
                )}
                <RadioGroup
                    field="colorPreview"
                    onChange={
                        !isMobile
                            ? undefined
                            : (event) => onChange(findColor(event.target.value, colorSwatches) ?? null)
                    }
                >
                    <div className={classes.swatchContainer}>
                        {displayColors.map((color) => (
                            <SwatchOption
                                key={color.color_id}
                                optionName={color.label}
                                labelClassName={classes.swatchLabel}
                                input={
                                    <Radio
                                        className={classes.swatchRadioInput}
                                        value={color.color_id}
                                        id={`swatch-${productId}-${color.color_id}`}
                                        data-value={color.label}
                                    />
                                }
                            >
                                <span
                                    className={classes.swatchBody}
                                    style={swatchCssNumerical({
                                        kind: color.kind as SwatchKindNumerical,
                                        value: color.value,
                                    })}
                                    data-value={color.label}
                                    onMouseEnter={!isMobile ? () => onChange(color) : undefined}
                                />
                            </SwatchOption>
                        ))}
                    </div>
                </RadioGroup>
                {colorSwatches.length > NR_OF_SWATCHES && (
                    <button
                        type="button"
                        className={classes.paginationButtonRight}
                        onClick={handleNextButtonClick}
                        disabled={lastIndex >= colorSwatches.length}
                        aria-label="Next colours"
                    >
                        <Svg name="arrow-right" className={classes.svg} />
                    </button>
                )}
            </div>
        </div>
    );
};
